<template>
  <div class="d-flex flex-column h-100">
    <div class="border-bottom bg-white flex-shrink-0">
      <div v-if="selectedIsland" class="p-3">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary mb-2"
          @click="$emit('back')"
          aria-label="Return to navigation list"
          ref="backButton"
        >
          <i class="bi bi-arrow-left me-1"></i>Back to list
        </button>
        <div>
          <h2 class="h5 fw-bold mb-0">
            {{ headerTitle }}
          </h2>
          <p v-if="indigenousName" class="text-muted mb-0">
            {{ indigenousName }}
          </p>
        </div>
        <p class="text-muted small mb-0 mt-2">
          {{ assignmentSummary }}
        </p>
      </div>
    </div>

    <!-- BEGIN MAIN CONTENT -->

    <div v-if="initialEnrichmentComplete" class="flex-grow-1 bg-white">
      <!-- Edit mode: Show form when user is editing their report -->
      <ReportEditForm
        v-if="selectedIsland && isEditMode && isAssignedToCurrentUser"
        :report="userReport"
        :fieldOrder="fieldOrder"
        @save="handleSaveReport"
        @cancel="handleExitEditMode"
      />

      <!-- Normal view: Show assignment actions and submissions -->
      <div v-else-if="selectedIsland" class="py-3">
        <div v-if="errorMessage" class="alert alert-danger mb-3" role="alert">
          {{ errorMessage }}
        </div>
        <AssignmentActions
          v-if="showAssignmentActions"
          :showAssignButton="showAssignButton"
          :canAssign="canAssignIsland"
          :assignLabel="assignButtonLabel"
          :assignDescription="assignmentDescription"
          :assignDisabledReason="assignDisabledReason"
          :showEditButton="isAssignedToCurrentUser"
          :showShareButton="isAssignedToCurrentUser"
          :isLoading="isLoading"
          @assign="showAssignmentConfirmation"
          @edit="handleEditReport"
        />

        <div
          v-if="shouldShowSubmissionSection"
          class="mb-4"
          data-testid="submission-section"
        >
          <h3 class="text-uppercase text-muted small mb-2 h6">
            Student submission<span v-if="shouldShowSubmissionTabs">s</span>
          </h3>
          <SubmissionTabs
            v-if="shouldShowSubmissionTabs"
            :selectedIndex="selectedReportIndex"
            :reports="assignedStudents"
            :currentUserReportIndex="currentUserReportIndex"
            @update:selectedIndex="handleReportIndexChange"
          />
          <SubmissionView
            v-if="activeSubmissionReport && activeSubmissionHasContent"
            :report="activeSubmissionReport"
            :fieldOrder="fieldOrder"
          />
          <div
            v-if="activeSubmissionReport && !activeSubmissionHasContent"
            class="alert alert-info mb-0"
          >
            Assigned student is still preparing their report.
          </div>
        </div>
      </div>
      <div v-else class="p-4 text-center text-muted">
        <p class="mb-0">
          Click an island marker to view existing reports or submit your own.
        </p>
      </div>
    </div>
    <div
      v-else
      class="flex-grow-1 bg-white d-flex align-items-center justify-content-center"
    >
      <div class="text-center text-muted">
        <div class="spinner-border mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mb-0">Loading island data...</p>
      </div>
    </div>

    <!-- Assignment Confirmation Modal -->
    <div
      class="modal fade"
      id="assignmentConfirmationModal"
      tabindex="-1"
      aria-labelledby="assignmentConfirmationModalLabel"
      aria-hidden="true"
      ref="confirmationModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="assignmentConfirmationModalLabel">
              Confirm Assignment
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-3">
              <strong>This action cannot be undone.</strong>
            </p>
            <p class="mb-0">
              By assigning yourself to this island, you are committing to submit
              a report. You will not be able to change your assignment after
              confirming.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmAssignment"
            >
              Confirm Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { IslandHeaders, FactSheetFields } from "@/types/index.ts";
import { Modal } from "bootstrap";
import SubmissionTabs from "@/components/activity/details/SubmissionTabs.vue";
import SubmissionView from "@/components/activity/details/SubmissionView.vue";
import AssignmentActions from "@/components/activity/details/AssignmentActions.vue";
import ReportEditForm from "@/components/activity/details/ReportEditForm.vue";
import DatastoreAPI from "@/helpers/DatastoreAPI";

export default {
  name: "SelectionDetails",
  components: {
    SubmissionTabs,
    SubmissionView,
    AssignmentActions,
    ReportEditForm
  },
  emits: ["share-report", "back"],
  mixins: [DatastoreAPI],
  data() {
    return {
      isEditMode: false,
      confirmationModal: null,
      errorMessage: "",
      isLoading: false
    };
  },
  computed: {
    ...mapState("datastore", ["entries"]),
    ...mapGetters([
      "mapToDatastore",
      "selectedItem",
      "getIslands",
      "getONID",
      "selectedReportIndex",
      "initialEnrichmentComplete"
    ]),
    selectedIsland() {
      return this.selectedItem || null;
    },
    assignedStudents() {
      if (!this.selectedIsland || !Array.isArray(this.selectedIsland.Reports)) {
        return [];
      }
      return this.selectedIsland.Reports;
    },
    maxAssignments() {
      if (!this.selectedIsland) return 0;
      const raw = this.selectedIsland[IslandHeaders.MAX_ASSIGNED_STUDENTS];
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    },
    assignedCount() {
      return this.assignedStudents.length;
    },
    availableSlots() {
      return Math.max(this.maxAssignments - this.assignedCount, 0);
    },
    isUnassigned() {
      return this.assignedCount === 0;
    },
    isFullyAssigned() {
      return (
        this.assignedCount >= this.maxAssignments && this.maxAssignments > 0
      );
    },
    isPartiallyAssigned() {
      return !this.isUnassigned && !this.isFullyAssigned;
    },
    reportsWithSubmission() {
      return this.assignedStudents.filter((report) =>
        this.reportHasContent(report)
      );
    },
    hasSubmissionData() {
      return this.reportsWithSubmission.length > 0;
    },
    shouldShowSubmissionSection() {
      return this.assignedCount > 0;
    },
    shouldShowSubmissionTabs() {
      return this.assignedCount > 1;
    },
    headerTitle() {
      if (!this.selectedIsland) return "";
      return (
        this.selectedIsland[FactSheetFields.WESTERN_NAME] ||
        this.selectedIsland[IslandHeaders.NAME] ||
        "Selected Island"
      );
    },
    indigenousName() {
      if (!this.selectedIsland) return "";
      return (
        this.selectedIsland[IslandHeaders.INDIGENOUS_NAME] ||
        this.selectedIsland[FactSheetFields.INDIGENOUS_NAME] ||
        ""
      );
    },
    assignmentSummary() {
      if (!this.selectedIsland) return "";
      const plural = this.maxAssignments === 1 ? "student" : "students";
      return `${this.assignedCount} of ${this.maxAssignments} ${plural} assigned`;
    },
    showAssignButton() {
      return (
        !!this.selectedIsland &&
        !this.isAssignedToCurrentUser &&
        !this.isFullyAssigned
      );
    },
    showAssignmentActions() {
      return this.showAssignButton || this.isAssignedToCurrentUser;
    },
    canAssignIsland() {
      return this.showAssignButton && !this.hasSelectedAnotherIsland;
    },
    assignButtonLabel() {
      if (this.isUnassigned) return "Assign myself to this island";
      if (this.isPartiallyAssigned) return "Assign myself to this island";
      return "Assign myself to this island";
    },
    assignmentDescription() {
      if (!this.selectedIsland) return "";
      if (this.isUnassigned) {
        return "This island is available for assignment.";
      }
      if (this.isPartiallyAssigned) {
        const slots = this.availableSlots;
        const plural = slots === 1 ? "spot" : "spots";
        return `${slots} ${plural} remaining for this island.`;
      }
      if (this.isFullyAssigned) {
        return "This island has reached the maximum number of assigned students.";
      }
      return "";
    },
    assignDisabledReason() {
      if (this.hasSelectedAnotherIsland && !this.isAssignedToCurrentUser) {
        return this.selectedElsewhereLabel;
      }
      if (this.isFullyAssigned && !this.isAssignedToCurrentUser) {
        return "No more assignment slots are available.";
      }
      return "";
    },
    fieldOrder() {
      return Object.values(FactSheetFields);
    },
    selectedIslandId() {
      return this.getIslandId(this.selectedIsland);
    },
    currentUserAssignmentIsland() {
      if (!this.getONID) return null;
      return this.getIslands.find((island) =>
        Array.isArray(island.Reports)
          ? island.Reports.some((report) => report?.onid === this.getONID)
          : false
      );
    },
    hasSelectedAnotherIsland() {
      if (!this.currentUserAssignmentIsland) return false;
      if (!this.selectedIslandId) return true;
      return (
        this.getIslandId(this.currentUserAssignmentIsland) !==
        this.selectedIslandId
      );
    },
    selectedElsewhereLabel() {
      if (!this.currentUserAssignmentIsland) {
        return "You are already assigned to another island.";
      }
      const name =
        this.currentUserAssignmentIsland[FactSheetFields.WESTERN_NAME] ||
        this.currentUserAssignmentIsland[IslandHeaders.NAME] ||
        "another island";
      return `You are already assigned to ${name}. You can only work on one island at a time.`;
    },
    isAssignedToCurrentUser() {
      if (!this.getONID || !this.selectedIsland) return false;
      return this.assignedStudents.some(
        (report) => report?.onid === this.getONID
      );
    },
    userReport() {
      if (!this.isAssignedToCurrentUser) return null;
      return (
        this.assignedStudents.find((report) => report?.onid === this.getONID) ||
        null
      );
    },
    showPendingNote() {
      return this.isFullyAssigned && !this.hasSubmissionData;
    },
    activeSubmissionReport() {
      if (!this.assignedStudents.length) return null;
      const index = this.selectedReportIndex ?? 0;
      const validIndex = Math.max(
        0,
        Math.min(index, this.assignedStudents.length - 1)
      );
      return this.assignedStudents[validIndex] || null;
    },
    activeSubmissionHasContent() {
      if (!this.activeSubmissionReport) return false;
      return this.reportHasContent(this.activeSubmissionReport);
    },
    currentUserReportIndex() {
      if (!this.getONID || !this.assignedStudents.length) return -1;
      const index = this.assignedStudents.findIndex(
        (report) => report?.onid === this.getONID
      );
      return index >= 0 ? index : -1;
    }
  },
  watch: {
    showAssignmentActions: {
      immediate: true,
      handler() {
        console.log(
          "showAssignmentActions changed",
          this.showAssignmentActions
        );
      }
    },
    selectedIsland: {
      immediate: true,
      handler() {
        // Exit edit mode when island selection changes
        this.isEditMode = false;
        this.errorMessage = "";
      }
    },
    selectedReportIndex: {
      handler(newIndex) {
        // Update URL when report index changes (only if not null)
        if (this.selectedIsland && newIndex !== null) {
          const islandId = this.getIslandId(this.selectedIsland);
          this.updateUrlParams(islandId, newIndex);
        }
      }
    },
    assignedStudents: {
      deep: true,
      handler(reports) {
        // Set index to null if no reports, otherwise validate bounds
        if (reports.length === 0) {
          this.selectReportByIndex(null);
        } else if (
          this.selectedReportIndex !== null &&
          this.selectedReportIndex >= reports.length
        ) {
          this.selectReportByIndex(0);
        }
      }
    }
  },
  mounted() {
    // Initialize Bootstrap modal
    if (this.$refs.confirmationModal) {
      this.confirmationModal = new Modal(this.$refs.confirmationModal);
    }
    // Focus the back button when component mounts for keyboard users
    this.$nextTick(() => {
      if (this.$refs.backButton) {
        this.$refs.backButton.focus();
      }
    });
    // Add document-level Escape key handler
    document.addEventListener("keydown", this.handleDocumentKeyDown);
  },
  beforeUnmount() {
    // Clean up modal instance
    if (this.confirmationModal) {
      this.confirmationModal.dispose();
      this.confirmationModal = null;
    }
    // Remove document-level Escape key handler
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
  },
  methods: {
    ...mapActions([
      "assignIsland",
      "updateReport",
      "mapEntriesFromDatastore",
      "selectReportByIndex"
    ]),
    getIslandId(island) {
      if (!island) return "";
      return island[IslandHeaders.ID] || "";
    },
    updateUrlParams(islandId, reportIndex) {
      const query = { ...this.$route.query };
      if (islandId) {
        query.island = islandId;
        // Only include reportIndex param if index is > 0, otherwise remove it
        if (
          reportIndex !== undefined &&
          reportIndex !== null &&
          reportIndex > 0
        ) {
          query.reportIndex = reportIndex.toString();
        } else {
          delete query.reportIndex;
        }
      } else {
        delete query.island;
        delete query.reportIndex;
      }
      // Only update if params actually changed to avoid navigation loops
      const currentQuery = this.$route.query;
      if (
        currentQuery.island !== query.island ||
        currentQuery.reportIndex !== query.reportIndex
      ) {
        this.$router.push({ query }).catch(() => {
          // Ignore navigation duplicate errors
        });
      }
    },
    handleReportIndexChange(index) {
      this.selectReportByIndex(index);
    },
    reportHasContent(report) {
      if (!report || !report.reportDetails) return false;
      return Object.values(report.reportDetails).some((value) => {
        if (Array.isArray(value)) {
          return value.some((entry) => entry && entry.toString().trim().length);
        }
        return value && value.toString().trim().length;
      });
    },
    showAssignmentConfirmation() {
      if (this.confirmationModal) {
        this.confirmationModal.show();
      }
    },
    confirmAssignment() {
      if (this.confirmationModal) {
        this.confirmationModal.hide();
      }
      this.handleAssignToIsland();
    },
    handleAssignToIsland() {
      const islandId = this.getIslandId(this.selectedIsland);
      if (!islandId) {
        console.error("Cannot assign island: no island ID found");
        return;
      }

      this.errorMessage = "";
      this.isLoading = true;

      // Refresh data to check for duplicates
      this.readEntries()
        .then(() => {
          // TODO: similar to behavior in App.vue, check if entries have changed since last read to determine if we need to update the global island with mapEntriesFromDatastore
          // Update local state with new entries
          this.mapEntriesFromDatastore(this.entries);

          // Find the island again to get latest status
          const updatedIsland = this.getIslands.find(
            (i) => this.getIslandId(i) === islandId
          );

          if (!updatedIsland) {
            this.errorMessage = "Island not found after update.";
            this.isLoading = false;
            return;
          }

          // Check if full
          const max =
            parseInt(updatedIsland[IslandHeaders.MAX_ASSIGNED_STUDENTS], 10) ||
            1;
          const current = updatedIsland.Reports
            ? updatedIsland.Reports.length
            : 0;

          if (current >= max) {
            this.errorMessage =
              "This island was just filled by another student. Please select another island.";
            // Cancel the assignment since the island is already full
            this.isLoading = false;
            return;
          }

          // First assign the island in the store
          this.assignIsland(islandId);
          // Then update the datastore
          this.updateEntry(this.mapToDatastore, this.readEntries)
            .then(() => {
              console.log("Island assigned and saved to datastore");
            })
            .catch((error) => {
              console.error("Error assigning island:", error);
              this.errorMessage =
                "Failed to save assignment. Please try again.";
            })
            .finally(() => {
              this.isLoading = false;
            });
        })
        .catch((err) => {
          console.error("Failed to refresh entries", err);
          this.errorMessage =
            "Network error. Please check your connection and try again.";
          this.isLoading = false;
        });
    },
    handleEditReport() {
      // Enter edit mode
      this.isEditMode = true;
    },
    handleExitEditMode() {
      // Exit edit mode
      this.isEditMode = false;
    },
    handleSaveReport(reportData) {
      // Update the report in Vuex store
      this.updateReport(reportData);
      // Save to datastore
      this.updateEntry(this.mapToDatastore, this.readEntries)
        .then(() => {
          console.log("Report saved to datastore");
          // Exit edit mode after successful save
          this.isEditMode = false;
        })
        .catch((error) => {
          console.error("Error saving report:", error);
        });
    },
    /**
     * Handles document-level keyboard events (for Escape key)
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleDocumentKeyDown(event) {
      if (event.key === "Escape") {
        // Don't handle Escape if:
        // - In edit mode (let edit form handle it)
        // - Modal is open (let modal handle it)
        // - Focus is in an input/textarea/select
        if (this.isEditMode) {
          return;
        }
        const target = event.target;
        if (
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT"
        ) {
          return;
        }
        // Check if modal is open
        if (
          this.confirmationModal &&
          this.$refs.confirmationModal &&
          this.$refs.confirmationModal.classList.contains("show")
        ) {
          return;
        }
        event.preventDefault();
        this.$emit("back");
      }
    }
  }
};
</script>

<style scoped>
/* All styling handled with Bootstrap utility classes */
</style>
